import { useSignInController, useSignOutController } from './session';
import { useMutation, useQuery } from '@tanstack/react-query';
import { networkApiCall } from '../lib/networking';

import {
  AUTH_ACCEPT_PATH,
  AUTH_BASE_PATH,
  AUTH_PASSWORD_END_POINT
} from '../lib/networking';
import { useAuth } from '../hooks/auth';
import { useMemo } from 'react';
import { getModuleInfo } from '../utils/models';

type AuthPayload = Record<string, unknown>;

export const useSignInAction = () => {
  const { logIn } = useAuth();
  return useSignInController({ onSignedIn: logIn });
};

export const useSignUpAction = () => {
  const { logIn } = useAuth();

  return useMutation({
    mutationFn: (data: AuthPayload) =>
      networkApiCall(AUTH_BASE_PATH, { method: 'post', data }),
    onSuccess: (data) => logIn(data!.data.data)
  });
};

export const useSignupAllowed = () => {
  return useMemo(() => getModuleInfo('rhino')?.allow_signup, []);
};

export const useApiKeysEnabled = () =>
  useMemo(() => getModuleInfo('rhino')?.api_keys_enabled ?? false, []);

// The ceiling (in days) on how long a new API key may live, or null for
// unlimited. The create form uses this to bound its expiry picker to options
// the backend will accept.
export const useApiKeyMaxExpiryDays = (): number | null =>
  useMemo(() => {
    const value = getModuleInfo('rhino')?.api_key_max_expiry_days;
    return typeof value === 'number' ? value : null;
  }, []);

export const useAcceptInvitationAction = () => {
  const { logIn } = useAuth();

  return useMutation({
    mutationFn: (data: AuthPayload) =>
      networkApiCall(AUTH_ACCEPT_PATH, { method: 'put', data }),
    onSuccess: (data) => {
      // Existing-account accepts return data:null (no session) — only log in
      // when the backend minted one.
      if (data?.data?.data) logIn(data.data.data);
    }
  });
};

// Invitation metadata for the accept page (invited email, password_required).
export const useInvitation = (invitationToken: string) =>
  useQuery({
    queryKey: ['invitation', invitationToken],
    queryFn: () =>
      networkApiCall(
        `${AUTH_ACCEPT_PATH}?invitation_token=${encodeURIComponent(
          invitationToken
        )}`
      ),
    enabled: !!invitationToken,
    retry: false
  });

export const useResendInvitationAction = () =>
  useMutation({
    mutationFn: (id: number) =>
      networkApiCall(`/api/users_role_invites/${id}/resend`, { method: 'post' })
  });

export const useSignOutAction = () => {
  const { logOut } = useAuth();
  return useSignOutController({
    onSignedOut: logOut
  });
};

export const useUserUpdateAction = () => {
  return useMutation({
    mutationFn: (data: AuthPayload) =>
      networkApiCall(AUTH_BASE_PATH, { method: 'patch', data: data })
  });
};

export const useForgotPasswordAction = () => {
  return useMutation({
    mutationFn: (data: AuthPayload) =>
      networkApiCall(AUTH_PASSWORD_END_POINT, { method: 'post', data })
  });
};

export const useResetPasswordAction = () => {
  const { logIn } = useAuth();
  return useMutation({
    mutationFn: ({
      data,
      headers
    }: {
      data: AuthPayload;
      headers?: Record<string, string>;
    }) =>
      networkApiCall(AUTH_PASSWORD_END_POINT, {
        method: 'patch',
        data,
        headers
      }),
    onSuccess: (data) => logIn(data!.data.data)
  });
};
